import * as Yup from 'yup';
import { MIN_DRIVER_AGE, MAX_DRIVER_AGE } from '@/constants/driverAge';

const validationSchema = Yup.object().shape({
    vehicle: Yup.object().nullable().required('Please select a vehicle'),
    collectionDate: Yup.date()
        .typeError('Please enter a valid collection date')
        .required('Collection date is required'),
    returnDate: Yup.date()
        .typeError('Return date must be a valid date')
        .required('Return date is required')
        .test(
            'is-after-collection',
            'Return date must be after collection date',
            function (value) {
                const { collectionDate } = this.parent;
                if (
                    !value ||
                    !collectionDate ||
                    !(collectionDate instanceof Date) ||
                    isNaN(collectionDate.getTime())
                ) {
                    return true; // Let required() and collectionDate's own validation handle missing values
                }
                return value >= collectionDate;
            }
        ),
    fullName: Yup.string().trim().required('Full name is required'),
    emailAddress: Yup.string()
        .trim()
        .lowercase()
        .email('Please enter a valid email address')
        .required('Email address is required'),
    confirmEmailAddress: Yup.string()
        .trim()
        .lowercase()
        .email('Please enter a valid email address')
        .oneOf([Yup.ref('emailAddress')], 'Email addresses do not match')
        .required('Confirm email address is required'),
    dateOfBirth: Yup.string()
        .required('Date of Birth is required')
        .test(
            'age-range',
            `Driver must be between ${MIN_DRIVER_AGE} and ${MAX_DRIVER_AGE} years old`,
            (value) => {
                if (!value) return false;
                const [day, month, year] = value.split('/');
                if (!day || !month || !year || year.length !== 4) return false;

                const d = Number(day),
                    m = Number(month),
                    y = Number(year);
                const birthDate = new Date(y, m - 1, d);

                // Guard: ensure the date didn't roll over (e.g. 31 Feb > March)
                if (
                    birthDate.getFullYear() !== y ||
                    birthDate.getMonth() !== m - 1 ||
                    birthDate.getDate() !== d
                ) {
                    return false;
                }

                const today = new Date();
                let age = today.getFullYear() - birthDate.getFullYear();
                const monthDiff = today.getMonth() - birthDate.getMonth();
                if (
                    monthDiff < 0 ||
                    (monthDiff === 0 && today.getDate() < birthDate.getDate())
                ) {
                    age--;
                }
                return age >= MIN_DRIVER_AGE && age <= MAX_DRIVER_AGE;
            }
        ),
    phoneNumber: Yup.string()
        .trim()
        .required('Phone number is required')
        .matches(/^\+?[\d\s\-().]{7,15}$/, 'Please enter a valid phone number'),
    claimConvictionHistory: Yup.string()
        .trim()
        .required('Claim/Conviction is required'),
    claimConvictionComments: Yup.string()
        .trim()
        .test(
            'required',
            'Details for motor insurance claims or convictions are required',
            (value, context) => {
                // If claimConvictionHistory is 'Yes', then a value must exist.
                // If it's 'No', the test passes (it's not required).
                return context.parent.claimConvictionHistory === 'Yes'
                    ? !!value // This will be `false` for an empty string, failing validation.
                    : true; // This will always pass.
            }
        ),
    comments: Yup.string(),
    previousHires: Yup.string()
        .oneOf(['Yes', 'No'], 'Please select an option')
        .required('This field is required'),
    occupation: Yup.string().test('required', 'Required', (value, context) => {
        return context.parent.previousHires === 'No' ? !!value : true;
    }),
    countryOfOrigin: Yup.string().test(
        'required',
        'Required',
        (value, context) => {
            return (
                // If the user said no regarding hired in the last 5 years,
                // then we check if they have entered any data,
                // making it required in that scenario.
                // More below.
                context.parent.previousHires === 'No' ? !!value : true
            );
        }
    ),
    countryDrivingLicence: Yup.string().test(
        'required',
        'Required',
        (value, context) => {
            return context.parent.previousHires === 'No' ? !!value : true;
        }
    ),
    yearTestPassed: Yup.string().test(
        'required',
        'Required',
        (value, context) => {
            return context.parent.previousHires === 'No' ? !!value : true;
        }
    ),
    address: Yup.string().test('required', 'Required', (value, context) => {
        return context.parent.previousHires === 'No' ? !!value : true;
    }),
});

export default validationSchema;
